// @px7digital/system-libraries/auth-overlay — styles
//
// One CSS string, injected once per document (guarded by an id). Every
// visual value is a CSS custom property under the `--px7-auth-*`
// namespace with a neutral default, so an app themes the whole overlay
// either by passing the `theme` prop (sets the vars inline on the root)
// or by overriding the vars in its own stylesheet under `.px7-auth`.
//
// Defaults are intentionally plain (near-black accent, white surface) so
// an unthemed overlay still looks deliberate. The point of the component
// is that GoDating, UrbanCrop, StyleSignal, etc. each pass their own
// accent / radius / font and get an overlay that reads as native to that
// app while sharing one battle-tested auth flow.

export const STYLE_ID = "px7-auth-overlay-styles";

export const CSS = `
.px7-auth {
  --px7-auth-font: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  --px7-auth-backdrop: rgba(18, 14, 10, 0.55);
  --px7-auth-surface: #ffffff;
  --px7-auth-ink: #18120c;
  --px7-auth-muted: #6c6a62;
  --px7-auth-border: rgba(0, 0, 0, 0.10);
  --px7-auth-field-bg: #ffffff;
  --px7-auth-field-border: rgba(0, 0, 0, 0.16);
  --px7-auth-field-radius: 12px;
  --px7-auth-accent: #111114;
  --px7-auth-accent-ink: #ffffff;
  --px7-auth-accent-hover: #2a2a30;
  --px7-auth-radius: 20px;
  --px7-auth-danger: #b42318;
  --px7-auth-danger-bg: rgba(180, 35, 24, 0.08);
  --px7-auth-notice-bg: rgba(0, 0, 0, 0.04);
  --px7-auth-shadow: 0 24px 60px -20px rgba(0, 0, 0, 0.45);
  --px7-auth-max-width: 420px;
  /* Unselected tab text sits on notice-bg (a tinted surface), so plain muted
     can drop below WCAG 4.5:1 depending on the app theme. Mixing muted toward
     ink keeps the quiet look but guarantees headroom on tinted backgrounds. */
  --px7-auth-seg-ink: color-mix(in srgb, var(--px7-auth-muted) 55%, var(--px7-auth-ink));

  font-family: var(--px7-auth-font);
  color: var(--px7-auth-ink);
  box-sizing: border-box;
}
.px7-auth *, .px7-auth *::before, .px7-auth *::after { box-sizing: border-box; }

.px7-auth__backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: var(--px7-auth-backdrop);
  -webkit-backdrop-filter: blur(3px);
  backdrop-filter: blur(3px);
  animation: px7-auth-fade 160ms ease-out;
  overflow-y: auto;
}

.px7-auth__card {
  position: relative;
  width: 100%;
  max-width: var(--px7-auth-max-width);
  background: var(--px7-auth-surface);
  border-radius: var(--px7-auth-radius);
  box-shadow: var(--px7-auth-shadow);
  padding: 28px 26px 24px;
  animation: px7-auth-pop 200ms cubic-bezier(0.16, 1, 0.3, 1);
}
.px7-auth--inline .px7-auth__card {
  box-shadow: none;
  animation: none;
  max-width: none;
  padding: 0;
  background: transparent;
}

.px7-auth__close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: var(--px7-auth-notice-bg);
  color: var(--px7-auth-muted);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: grid;
  place-items: center;
}
.px7-auth__close:hover { color: var(--px7-auth-ink); }

.px7-auth__head { text-align: center; margin-bottom: 18px; }
.px7-auth__logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  margin-bottom: 12px;
}
.px7-auth__logo img, .px7-auth__logo svg { height: 44px; width: auto; display: block; }
.px7-auth__title {
  margin: 0;
  font-size: 22px;
  font-weight: 680;
  letter-spacing: -0.01em;
}
.px7-auth__subtitle {
  margin: 6px auto 0;
  max-width: 32ch;
  font-size: 14px;
  line-height: 1.45;
  color: var(--px7-auth-muted);
}

.px7-auth__seg {
  display: flex;
  gap: 4px;
  padding: 4px;
  margin: 0 0 18px;
  background: var(--px7-auth-notice-bg);
  border-radius: 999px;
}
.px7-auth__seg-btn {
  flex: 1;
  border: none;
  background: transparent;
  padding: 9px 12px;
  border-radius: 999px;
  font: inherit;
  font-size: 14px;
  font-weight: 560;
  color: var(--px7-auth-seg-ink);
  cursor: pointer;
  transition: background 140ms ease, color 140ms ease;
}
.px7-auth__seg-btn[aria-selected="true"] {
  background: var(--px7-auth-surface);
  color: var(--px7-auth-ink);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.px7-auth__form { display: flex; flex-direction: column; gap: 14px; }
.px7-auth__field { display: flex; flex-direction: column; gap: 6px; }
.px7-auth__label { font-size: 13px; font-weight: 560; color: var(--px7-auth-ink); }
.px7-auth__input {
  width: 100%;
  height: 46px;
  padding: 0 14px;
  font: inherit;
  font-size: 15px;
  color: var(--px7-auth-ink);
  background: var(--px7-auth-field-bg);
  border: 1px solid var(--px7-auth-field-border);
  border-radius: var(--px7-auth-field-radius);
  outline: none;
  transition: border-color 140ms ease, box-shadow 140ms ease;
}
.px7-auth__input:focus {
  border-color: var(--px7-auth-accent);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--px7-auth-accent) 18%, transparent);
}
.px7-auth__input[aria-invalid="true"] { border-color: var(--px7-auth-danger); }

.px7-auth__pw-wrap { position: relative; }
.px7-auth__pw-wrap .px7-auth__input { padding-right: 58px; }
.px7-auth__pw-toggle {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: var(--px7-auth-muted);
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
}
.px7-auth__pw-toggle:hover { color: var(--px7-auth-ink); }

.px7-auth__submit {
  height: 48px;
  margin-top: 4px;
  border: none;
  border-radius: var(--px7-auth-field-radius);
  background: var(--px7-auth-accent);
  color: var(--px7-auth-accent-ink);
  font: inherit;
  font-size: 15px;
  font-weight: 620;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 140ms ease, transform 80ms ease;
}
.px7-auth__submit:hover:not(:disabled) { background: var(--px7-auth-accent-hover); }
.px7-auth__submit:active:not(:disabled) { transform: translateY(1px); }
.px7-auth__submit:disabled { opacity: 0.65; cursor: default; }

.px7-auth__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid color-mix(in srgb, var(--px7-auth-accent-ink) 40%, transparent);
  border-top-color: var(--px7-auth-accent-ink);
  border-radius: 50%;
  animation: px7-auth-spin 700ms linear infinite;
}

.px7-auth__error {
  margin: 0;
  padding: 10px 12px;
  font-size: 13.5px;
  line-height: 1.4;
  color: var(--px7-auth-danger);
  background: var(--px7-auth-danger-bg);
  border-radius: 10px;
}
.px7-auth__notice {
  margin: 0;
  padding: 10px 12px;
  font-size: 13.5px;
  line-height: 1.4;
  color: var(--px7-auth-ink);
  background: var(--px7-auth-notice-bg);
  border-radius: 10px;
}

.px7-auth__row {
  display: flex;
  justify-content: center;
  margin-top: 2px;
}
.px7-auth__link {
  border: none;
  background: transparent;
  font: inherit;
  font-size: 13px;
  color: var(--px7-auth-muted);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  padding: 2px 4px;
}
.px7-auth__link:hover { color: var(--px7-auth-ink); }

.px7-auth__divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 16px 0 12px;
  color: var(--px7-auth-muted);
  font-size: 12px;
}
.px7-auth__divider::before, .px7-auth__divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--px7-auth-border);
}

.px7-auth__sso {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 44px;
  border: 1px solid var(--px7-auth-field-border);
  border-radius: var(--px7-auth-field-radius);
  background: var(--px7-auth-surface);
  color: var(--px7-auth-ink);
  font: inherit;
  font-size: 14px;
  font-weight: 560;
  text-decoration: none;
  cursor: pointer;
}
.px7-auth__sso:hover { background: var(--px7-auth-notice-bg); }

.px7-auth__foot {
  margin: 16px 0 0;
  text-align: center;
  font-size: 12px;
  line-height: 1.5;
  color: var(--px7-auth-muted);
}

@keyframes px7-auth-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes px7-auth-pop {
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes px7-auth-spin { to { transform: rotate(360deg); } }

@media (prefers-reduced-motion: reduce) {
  .px7-auth__backdrop, .px7-auth__card { animation: none; }
  .px7-auth__spinner { animation-duration: 1200ms; }
}
`;

/**
 * Inject the overlay stylesheet once. No-op on the server or after the
 * first call. Safe to call on every render.
 */
export function ensureStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}
